/**
 * WHO IS SIGNED IN, on Supabase.
 *
 * The sibling of auth.ts, and the same eight things: send a link, finish a
 * link, sign in with a password, set one, sign out, ask who is here, be told
 * when that changes, and re-read the account.
 *
 * THE ONE REAL DIFFERENCE IN SHAPE, and it is an improvement. Firebase hands
 * back an unexplained sign-in email that cannot be reworded — the message that
 * tells a new person "if you did not request this link, you can safely ignore
 * this email", which is the recorded launch blocker. Supabase's templates are
 * editable, so that blocker is answered by a settings screen rather than by
 * building a server to send mail. It is not answered HERE, though: this file
 * sends the link and the wording lives in the project's own settings, so the
 * blocker stays open until somebody edits it there.
 *
 * WHAT "VERIFIED" MEANS ON EACH. Firebase has emailVerified; Supabase records
 * `email_confirmed_at`, a time rather than a flag. Clicking a sign-in link
 * confirms the address, which is the same rule both sides — somebody who has
 * proved they can read the inbox. The policies read it out of the token, so
 * this is not merely cosmetic: an unconfirmed address sees nothing at all.
 */
import type { SupabaseClient, User } from '@supabase/supabase-js';
import type { Account, Accounts, AuthError, AuthProblem } from './auth';
import { rememberSignInMethod } from '../sessions';

/**
 * Supabase's failures, in words.
 *
 * IT REPORTS BY MESSAGE MORE THAN BY CODE, which is exactly the coupling the
 * boundary exists to contain: matching English in the app would be worse than
 * matching a code, and neither belongs above this file. `invalid_credentials`
 * deliberately does not say whether the account exists — the same choice
 * Firebase made, and defeating it with a more helpful message would hand an
 * attacker a way to discover who has an account.
 */
function reasonFor(message: string, code?: string): AuthProblem {
  const m = `${code ?? ''} ${message}`.toLowerCase();
  /*
   * ORDER MATTERS HERE, AND GETTING IT WRONG IS NOT COSMETIC.
   *
   * "Email link is invalid or has expired" contains both "email" and
   * "invalid", so a loose bad-address pattern placed first claimed it — and
   * somebody whose link had simply timed out was told their email address was
   * malformed. They would have checked the address, found it correct, and had
   * nowhere to go. The most specific reading goes first, and the address check
   * now insists on the words that only an address complaint uses.
   */
  if (/expired|otp_expired|invalid.*(token|link|otp)|(token|link|otp).*invalid/.test(m)) return 'link-expired';
  if (/invalid login|invalid credentials|invalid_credentials/.test(m)) return 'wrong-password';
  if (/email address|email.*format|malformed|invalid_email/.test(m)) return 'bad-email';
  if (/user not found|no user/.test(m)) return 'no-account';
  if (/redirect|not allowed|url.*not.*permitted/.test(m)) return 'untrusted-address';
  if (/rate limit|too many|over_.*_rate/.test(m)) return 'too-many';
  if (/reauthentication|recent login/.test(m)) return 'needs-recent-sign-in';
  if (/password.*(short|weak)|weak_password/.test(m)) return 'weak-password';
  return 'unknown';
}

function asAuthError(e: unknown): AuthError {
  const message = (e as { message?: string })?.message ?? String(e);
  const code = (e as { code?: string })?.code;
  const err = new Error(message) as AuthError;
  err.reason = reasonFor(message, code);
  err.code = code ?? message;
  return err;
}

const raise = (e: unknown): never => {
  throw asAuthError(e);
};

/**
 * The two fields the app uses, and nothing else.
 *
 * Supabase's User has a good forty members. Handing it out would put a
 * vendor's shape into the app's own state, which is the promise that makes
 * "we could move" turn out to be untrue on the day — and it is the thing the
 * boundary test counts.
 */
const asAccount = (u: User | null): Account | null =>
  u ? { email: u.email ?? null, emailVerified: !!u.email_confirmed_at } : null;

export function supabaseAccounts(client: SupabaseClient): Accounts {
  /*
   * READ FROM THE SESSION THE CLIENT ALREADY HOLDS, rather than asked for.
   * `current()` is called constantly and synchronously — the store asks it on
   * every recompute — and an await there would turn every one into a network
   * round trip. The client keeps the session in memory and refreshes it
   * itself; this is a look at that, kept current by onChange below.
   */
  let held: Account | null = null;
  void client.auth.getSession().then(({ data }) => {
    held = asAccount(data.session?.user ?? null);
  });

  return {
    current: () => held,

    /**
     * TOLD ONLY WHEN IT IS ACTUALLY SOMEBODY ELSE.
     *
     * WHAT HAPPENED WITHOUT THIS. Leaving FlightDeck in a background tab and
     * coming back to it drew "Loading the board…" again, on a session that had
     * never gone anywhere. #266. The client refreshes its token on a timer,
     * browsers stop timers in a hidden tab, so the refresh that fell due while
     * the tab was in the background arrives the moment it comes forward — and
     * every event was handed on identically, so being given a slightly newer
     * token for the same person was treated as a new person arriving.
     *
     * WHAT THAT COST. The listener in store.ts tears down every subscription,
     * puts `profilesKnown` and `projectsKnown` back to false and empties the
     * board. Where the kept copy answers, the blank is brief; where it does
     * not, somebody watches their board disappear and come back for no reason
     * they can see. Every gate is re-evaluated too, which is one plausible way
     * for #255 to happen — a listener attached twice.
     *
     * THE CHECK IS THE ADDRESS, NOT THE EVENT NAME. Suppressing
     * `TOKEN_REFRESHED` by name alone would be a list to keep in step with
     * somebody else's library: Supabase also emits `SIGNED_IN` when a tab
     * regains focus, which looks like a sign-in and is not one. Asking who it
     * is instead answers the real question — the same address is the same
     * person, whatever brought the event — and needs no maintenance when the
     * set of events changes.
     *
     * WHAT IS DELIBERATELY STILL PASSED ON. A first event of any kind, because
     * nothing has been said yet and the app is waiting to be told. A different
     * address, which is a real change of person. `USER_UPDATED`, which is the
     * event that fires when the address itself changes and is therefore the one
     * case where the same person must still be re-read. And a sign-out, where
     * the address becomes null and so differs from whatever was there.
     */
    onChange: (fn) => {
      /* undefined = nothing said yet, which is NOT the same as null = signed out. */
      let told: string | null | undefined;
      const { data } = client.auth.onAuthStateChange((event, session) => {
        held = asAccount(session?.user ?? null);
        const who = held?.email?.toLowerCase() ?? null;
        if (told !== undefined && who === told && event !== 'USER_UPDATED') return;
        told = who;
        fn(held);
      });
      return () => data.subscription.unsubscribe();
    },

    /**
     * A LINK THAT SIGNS THEM IN, and no password to invent.
     *
     * `shouldCreateUser` is left on: an invitation is the first time somebody
     * exists, and requiring an account first would make inviting anybody
     * impossible. Whether they may then SEE anything is a separate question
     * with a separate answer — the policies — and being able to sign in is
     * not being able to read.
     */
    /**
     * A LINK THAT SIGNS THEM IN — AND ONLY IF THEY ALREADY EXIST.
     *
     * `shouldCreateUser` used to be ON, with the reasoning that an invitation is
     * the first time somebody exists and requiring an account first would make
     * inviting anybody impossible. That reasoning stopped being true the day the
     * invite path was built: an account is now made deliberately, by an admin,
     * on the server.
     *
     * What it left behind was a hole. ANYBODY who typed ANY address into the
     * sign-in box was sent a working link and given a real account. They could
     * not see this workspace — the policies saw to that — but they should never
     * have had an account at all, and every one of them was a real row in a
     * table that only administrators are supposed to add to.
     *
     * Off, an unknown address gets nothing.
     */
    sendSignInLink: async (email, returnTo, captcha) => {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: returnTo, shouldCreateUser: false, captchaToken: captcha },
      });
      /*
       * AND THE REFUSAL MUST NOT SAY WHICH. "No such account" turns the sign-in
       * box into a way of asking whether somebody works here, one address at a
       * time — the same reason a wrong password does not say whether the address
       * was right. The screen says a link is on its way if the address is known,
       * which is true and tells an outsider nothing.
       */
      /*
       * AN ADDRESS NOBODY HAS ADDED IS TURNED AWAY, AND TOLD SO.
       *
       * This used to swallow the refusal and let the screen say "if there is an
       * account, a link is on its way" — the standard defence against using a
       * sign-in box to discover who works somewhere, one address at a time.
       *
       * CHANGED ON VINNIE'S INSTRUCTION, 11 August, and the trade is worth
       * writing down because it goes both ways. Nobody could ever get IN this
       * way — `shouldCreateUser: false` above means no account is made and no
       * link is sent, and that has always been true. What the old wording did
       * was mislead the honest half of the people who see it: somebody a
       * colleague forgot to add waited for an email that was never coming and
       * concluded the product was broken. What it protected against was an
       * outsider learning that an address IS known here. With no way to sign
       * yourself up, everyone who can sign in was deliberately added by an
       * administrator, and being told plainly that you were not is the more
       * useful truth. The enumeration risk is real and is now accepted rather
       * than overlooked — a rate limit is the thing that mitigates it and it is
       * on the list.
       *
       * `otp_disabled` IS WHAT AN UNKNOWN ADDRESS RETURNS — "Signups not allowed
       * for otp" — because we asked not to create one. Checked against the real
       * server rather than guessed; the wording reads like a global setting and
       * is not one, since known addresses get their link.
       */
      if (error && /user not found|signups not allowed|otp_disabled|no user/i.test(`${error.code ?? ''} ${error.message}`)) {
        raise({ message: 'no user found', code: 'user-not-found' });
      }
      if (error) {
        raise(error);
      }
    },

    /**
     * Whether this page was opened from one.
     *
     * Two shapes, because Supabase has had two: a `code` to exchange, and the
     * older tokens in the fragment. Both are checked so a link sent before a
     * settings change still works — an invitation somebody opens a day late
     * should not fail because the project moved on.
     */
    isSignInLink: (url) => {
      try {
        const u = new URL(url);
        return (
          u.searchParams.has('code') ||
          u.searchParams.has('token_hash') ||
          /access_token=|type=magiclink|type=recovery/.test(u.hash)
        );
      } catch {
        return false;
      }
    },

    /**
     * Finish it.
     *
     * The address is not needed — Supabase carries it in the link, unlike
     * Firebase, which asks for it back to prove the same browser opened it.
     * The parameter stays because the interface is shared and the Firebase
     * side genuinely needs it; ignoring it here is the honest implementation
     * rather than a reason to widen the contract.
     */
    completeSignInLink: async (_email, url) => {
      const u = new URL(url);

      /*
       * A LINK ON OUR OWN DOMAIN, WHICH IS THE ONLY KIND WE SEND NOW.
       *
       * The emails used to carry {{ .ConfirmationURL }} — an address at
       * Supabase's domain, which is a stranger's name on the one link somebody
       * is being asked to trust, in a message that has just told them who it is
       * from. The templates now build the link themselves from the token, so it
       * reads flightdeck.build, and the redemption that Supabase's own address
       * would have done happens here instead.
       *
       * `isSignInLink` has recognised `token_hash` since it was written and
       * nothing ever finished one — it fell through to the session check below
       * and reported a perfectly good link as expired. Nothing had sent one
       * yet, so nothing had noticed.
       */
      const hash = u.searchParams.get('token_hash');
      if (hash) {
        /*
         * The KIND matters: the same token means different things to the
         * server, and an invitation redeemed as a magic link is refused.
         * Defaulted rather than demanded — an older link may not carry it, and
         * a sign-in link is what most of them are.
         */
        const kind = (u.searchParams.get('type') || 'magiclink') as 'magiclink' | 'invite' | 'recovery' | 'email';
        const { error } = await client.auth.verifyOtp({ token_hash: hash, type: kind });
        if (error) raise(error);
        rememberSignInMethod('link');
        return;
      }

      const code = u.searchParams.get('code');
      if (code) {
        const { error } = await client.auth.exchangeCodeForSession(code);
        if (error) raise(error);
        rememberSignInMethod('link');
        return;
      }

      /*
       * AN INVITATION ARRIVES THE OTHER WAY, AND THE CLIENT WILL NOT READ IT.
       *
       * A link the app asked for comes back as a `code` above, because the
       * browser kept the matching half of the handshake when it asked. An
       * INVITATION was made on a server for somebody who has never opened this
       * site, so there is no matching half anywhere and Supabase can only hand
       * the tokens straight over — in the fragment, the older way.
       *
       * The client is set to the newer way for everything it starts itself,
       * which is the safer choice and stays. The cost is that it then REFUSES
       * to read a fragment it did not ask for: it left the tokens in the
       * address bar, kept nothing, and the app reported the link as already
       * used. Postgres disagreed — the account was confirmed and a session
       * existed — which is how this was found.
       *
       * So the tokens are read here and handed to the client directly. Nothing
       * is trusted that was not just minted by Supabase's own verify endpoint:
       * setSession checks them and fails if they are not real.
       */
      const frag = new URLSearchParams(u.hash.replace(/^#/, ''));
      const access_token = frag.get('access_token');
      const refresh_token = frag.get('refresh_token');
      if (access_token && refresh_token) {
        const { error } = await client.auth.setSession({ access_token, refresh_token });
        if (error) raise(error);
        rememberSignInMethod('link');
        return;
      }

      // Nothing usable in the address at all. If a session appeared anyway the
      // client read it as it loaded; if not, the link was no good.
      const { data } = await client.auth.getSession();
      if (!data.session) raise({ message: 'otp_expired', code: 'otp_expired' });
    },

    // The address travels inside the link, so asking for it again would only
    // be a hurdle — see the note on the interface.
    linkNeedsEmail: false,

    /*
     * Through a function that runs as its owner and is hard-wired to the caller
     * — see my_workspaces() in 0011. It takes no arguments, so there is nothing
     * to point at somebody else.
     */
    /*
     * Through company_at() in 0066 — security definer, because the caller is
     * outside the company by definition. It answers with the name and nothing
     * else, which is the whole disclosure and was decided deliberately.
     */
    companyAt: async (slug) => {
      const { data, error } = await client.rpc('company_at', { want: slug });
      if (error) raise(error);
      const found = (data ?? [])[0] as { name?: string; exists_here?: boolean } | undefined;
      return { name: found?.name ?? '', here: found?.exists_here === true };
    },

    /*
     * Through a function rather than a write, for the same reason inviting is:
     * the person asking is outside the company, so there is no row they are
     * allowed to create there. The function runs as the service and decides.
     */
    askToJoin: async (slug) => {
      const { data, error } = await client.functions.invoke('ask-to-join', { body: { company: slug } });
      if (error) {
        // The real reason is in the body; error.message says only "non-2xx".
        const ctx = (error as { context?: Response }).context;
        let said = error.message;
        try {
          const body = await ctx?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the original message; it is all there is.
        }
        raise({ message: said, code: 'ask-refused' });
      }
      const said = (data ?? {}) as { emailed?: boolean; again?: boolean };
      return { emailed: said.emailed === true, again: said.again === true };
    },

    /*
     * The immediate nudge to admins when a request is filed (#359). Only the
     * requestId travels; the function reads the row and the admins for itself,
     * and refuses anyone but the person who filed it. A failure is not raised —
     * the row is already written and the in-app queue already shows it.
     */
    notifyRequest: async (requestId) => {
      const { data } = await client.functions.invoke('notify-request', { body: { requestId } });
      const said = (data ?? {}) as { emailed?: boolean };
      return { emailed: said.emailed === true };
    },

    notifyDecision: async (requestId) => {
      const { data } = await client.functions.invoke('notify-request', {
        body: { requestId, phase: 'decided' },
      });
      const said = (data ?? {}) as { emailed?: boolean };
      return { emailed: said.emailed === true };
    },

    workspaces: async () => {
      const { data, error } = await client.rpc('my_workspaces');
      if (error) raise(error);
      return (
        (data ?? []) as {
          id: string;
          name: string;
          is_admin: boolean;
          visiting?: boolean;
          suspended?: boolean;
          suspended_why?: string | null;
          slug?: string | null;
        }[]
      ).map((w) => ({
        id: w.id,
        name: w.name,
        isAdmin: w.is_admin,
        // The address it answers on — #218. Empty rather than undefined, so a
        // link built from it is visibly wrong rather than quietly `/null/`.
        slug: w.slug ?? '',
        // A workspace reached through a support grant rather than a membership
        // — see 0013. Marked so the switcher can say so instead of presenting
        // somebody else's company as one of yours.
        visiting: w.visiting === true,
        /*
         * SWITCHED OFF, AND STILL LISTED. A suspended workspace shows nothing at
         * all, because my_tenant() will not name it — so if it were simply
         * hidden here, somebody whose only workspace has been suspended would
         * sign in to a blank screen with nothing to click and no explanation.
         * That is the frightened-customer failure suspension was designed to
         * avoid, so it is listed, marked, and carries the reason. See 0028.
         */
        suspended: w.suspended === true,
        suspendedWhy: w.suspended_why ?? '',
      }));
    },

    /*
     * TWO STEPS, AND THE SECOND IS NOT OPTIONAL. The function writes the choice
     * onto the account; the token in hand was minted before that and still
     * names the old workspace, so it is renewed here before this resolves.
     * Without the renewal the switch appears to do nothing for up to an hour,
     * which is the kind of fault that gets investigated from the wrong end.
     */
    chooseWorkspace: async (id) => {
      const { error } = await client.functions.invoke('workspace', { body: { workspace: id } });
      if (error) {
        // The real reason is in the body; error.message says only "non-2xx".
        const ctx = (error as { context?: Response }).context;
        let said = error.message;
        try {
          const body = await ctx?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the original message; it is all there is.
        }
        raise({ message: said, code: 'workspace-refused' });
      }
      const { error: stale } = await client.auth.refreshSession();
      if (stale) raise(stale);
    },

    canStartWorkspace: true,

    /*
     * The same two steps as choosing one, for the same reason: the server
     * writes it and the token in hand does not know yet. The caller reloads
     * afterwards — there is a whole app to build that currently believes this
     * person belongs nowhere.
     */
    startWorkspace: async (name) => {
      const { error } = await client.functions.invoke('start-workspace', { body: { name } });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        let said = error.message;
        try {
          const body = await ctx?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the original message; it is all there is.
        }
        raise({ message: said, code: 'workspace-refused' });
      }
      const { error: stale } = await client.auth.refreshSession();
      if (stale) raise(stale);
    },

    signInWithPassword: async (email, password, captcha) => {
      const { error } = await client.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: captcha },
      });
      if (error) raise(error);
      rememberSignInMethod('password');
    },

    /**
     * Supabase's recovery email, which uses the template we wrote — the one
     * whose last line says the password does NOT change unless the link is
     * opened. That sentence matters here more than anywhere: this email is the
     * one most likely to arrive for somebody who did not ask for it.
     *
     * Quiet about whether the address is known, for the reason on the interface.
     */
    sendPasswordReset: async (email, returnTo, captcha) => {
      const { error } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: returnTo,
        captchaToken: captcha,
      });
      if (error && !/user not found|not found/i.test(`${error.code ?? ''} ${error.message}`)) raise(error);
    },

    setPassword: async (password) => {
      const { error } = await client.auth.updateUser({ password });
      if (error) raise(error);
    },

    signOut: async () => {
      await client.auth.signOut();
    },

    sendVerification: async () => {
      const email = held?.email;
      if (!email) return;
      const { error } = await client.auth.resend({ type: 'signup', email });
      if (error) raise(error);
    },

    /**
     * Re-read the account AND take a fresh proof of identity.
     *
     * Both halves, for the reason written on the interface: the policies read
     * `email_verified` out of the TOKEN, not out of the account, so confirming
     * an address without taking a new token leaves the database still refusing
     * somebody the screen has just told is verified.
     */
    refresh: async () => {
      const { data, error } = await client.auth.refreshSession();
      if (error || !data.session) {
        const { data: back } = await client.auth.getUser();
        held = asAccount(back.user ?? null);
        return { emailVerified: held?.emailVerified ?? false };
      }
      held = asAccount(data.session.user);
      return { emailVerified: held?.emailVerified ?? false };
    },

    /**
     * Handed to the `invite` function, which runs on Supabase's servers.
     *
     * The caller's own token goes with it and is what the function decides
     * everything from — who they are, which workspace is theirs, and whether
     * their standing allows what they are asking for. Nothing here is trusted
     * by the other end, which is why this can be so short.
     */
    invite: async (email, role, options) => {
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      if (!token) raise({ message: 'no user found', code: 'user-not-found' });

      const { data: out, error } = await client.functions.invoke('invite', {
        body: {
          email,
          role,
          board: options?.board,
          redirectTo: options?.returnTo,
          boards: options?.boards,
          tempPassword: options?.tempPassword,
          assistant: options?.assistant,
          requestId: options?.requestId,
        },
      });
      /*
       * A refusal arrives as an error whose real message is in the response
       * body, not in `error.message` — which says only "non-2xx status code".
       * Showing that to somebody instead of "Only a manager or an admin can
       * invite someone" is the difference between a usable screen and a
       * mystery.
       */
      if (error) {
        const ctx = (error as { context?: Response }).context;
        let said = error.message;
        try {
          const body = await ctx?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the original message; it is all there is.
        }
        raise({ message: said, code: 'invite-refused' });
      }
      return out as { added: boolean; emailed: boolean; reason?: string; tempPassword?: string };
    },

    /**
     * Handed to the `change-email` function, which runs on Supabase's servers
     * and gates admin-only there exactly as `invite` does. The caller's token
     * carries their standing; the x-flightdeck-company header (set globally on
     * the client) pins the workspace. A refusal arrives as an error whose real
     * message is in the response body, not in `error.message` — unwrapped here
     * the same way invite does, so the admin reads "Only an admin can correct an
     * address" rather than "non-2xx status code".
     */
    changeEmail: async (oldEmail, newEmail) => {
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      if (!token) raise({ message: 'no user found', code: 'user-not-found' });

      const { error } = await client.functions.invoke('change-email', {
        body: { old: oldEmail, new: newEmail },
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        let said = error.message;
        try {
          const body = await ctx?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the original message; it is all there is.
        }
        raise({ message: said, code: 'change-email-refused' });
      }
    },
  } satisfies Accounts;
}
